import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getRecipe, updateRecipe } from "../api";
import { TextField, Button, Typography, Box, Alert } from '@mui/material';

const EditRecipePage = () => {
  const { id } = useParams(); // Récupère l'ID de la recette depuis l'URL
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    receipt: "",
    ingredients: "",
    servings: "",
    preparationTime: "",
    cookingTime: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const recipe = await getRecipe(id);
        setFormData({
          name: recipe.name,
          description: recipe.description,
          receipt: recipe.receipt.join(", "),
          ingredients: recipe.ingredients
            .map((item) => `${item.name} - ${item.quantity}`)
            .join(", "),
          servings: recipe.servings,
          preparationTime: recipe.preparationTime,
          cookingTime: recipe.cookingTime,
          image: null, // L'image ne peut pas être pré-remplie
        });
      } catch (err) {
        setError(err.message);
      }
    };

    fetchRecipe();
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("description", formData.description);
    formDataToSend.append(
      "receipt",
      JSON.stringify(formData.receipt.split(",").map((step) => step.trim()))
    );
    formDataToSend.append(
      "ingredients",
      JSON.stringify(
        formData.ingredients.split(",").map((item) => {
          const [name, quantity] = item.split("-").map((str) => str.trim());
          return { name, quantity };
        })
      )
    );
    formDataToSend.append("servings", formData.servings);
    formDataToSend.append("preparationTime", formData.preparationTime);
    formDataToSend.append("cookingTime", formData.cookingTime);
    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }

    try {
      await updateRecipe(id, formDataToSend);
      alert("Recette mise à jour avec succès !");
      navigate("/profile"); // Redirige vers le profil après la mise à jour
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="form-container" style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <Typography variant="h4" gutterBottom>Modifier une Recette</Typography>
      
      {error && <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* Recipe Name */}
        <TextField
          fullWidth
          label="Nom"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          name="description"
          multiline
          rows={4}
          value={formData.description}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Instructions */}
        <TextField
          fullWidth
          label="Instructions (séparées par des virgules)"
          name="receipt"
          multiline
          rows={4}
          value={formData.receipt}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Ingredients */}
        <TextField
          fullWidth
          label="Ingrédients (format : 'Tomate - 2, Oignon - 1')"
          name="ingredients"
          multiline
          rows={4}
          value={formData.ingredients}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Servings */}
        <TextField
          fullWidth
          label="Nombre de personnes"
          type="number"
          name="servings"
          value={formData.servings}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Preparation Time */}
        <TextField
          fullWidth
          label="Temps de préparation (en minutes)"
          type="number"
          name="preparationTime"
          value={formData.preparationTime}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Cooking Time */}
        <TextField
          fullWidth
          label="Temps de cuisson (en minutes)"
          type="number"
          name="cookingTime"
          value={formData.cookingTime}
          onChange={handleChange}
          required
          sx={{ marginBottom: '15px' }}
        />

        {/* Image Upload */}
        <Box sx={{ marginBottom: '15px' }}>
          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="image-upload"
          />
          <label htmlFor="image-upload">
            <Button variant="outlined" component="span" fullWidth>
              {formData.image ? 'Changer l\'image' : 'Télécharger une image'}
            </Button>
          </label>
        </Box>

        {/* Image Preview */}
        {imagePreview && (
          <Box sx={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
            <img src={imagePreview} alt="Prévisualisation" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
          </Box>
        )}

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          sx={{
            padding: '10px',
            fontSize: '16px',
            textTransform: 'none',
          }}
        >
          Enregistrer
        </Button>
      </form>
    </div>
  );
};

export default EditRecipePage;